// Daily NERV Trends from Variant History (FIG-238 · UC-257), shared by the client and the
// Deno function `variant-history-nerv-v1`, so it imports only the dependency-free ptDates
// module (edge functions import it as '../../../src/shared/variantHistoryNerv.ts').
//
// The chart plots only what the snapshot store holds: one stored ∑: NERV per merchant per
// PT day. A day with no usable snapshot is a gap (null), never a recalculated value.

import { addDays, clampAsOfToYesterday, isYmd, ptYesterday } from './ptDates.ts';
import type { NervChartPoint, NervRangeResult, VariantHistoryNervRow } from './types/variantHistoryNerv.ts';

/** Most days one read may cover: two years of retention (UC-254) is 731 days at most. */
export const MAX_VARIANT_HISTORY_NERV_DAYS = 731;

/** Days in the chart's default range, ending yesterday PT (inclusive). */
export const DEFAULT_NERV_RANGE_DAYS = 7;

/** True for a real calendar date written 'YYYY-MM-DD' ('2026-02-30' is not). */
export function isCalendarYmd(value: unknown): value is string {
  return isYmd(value) && addDays(value, 0) === value;
}

/** Whole days from `a` to `b` (calendar arithmetic, DST-free). */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/**
 * The days a request may read: both dates are PT calendar dates, the end is capped at
 * yesterday PT (today and later are never read), and the span is at most
 * MAX_VARIANT_HISTORY_NERV_DAYS. A start after the (capped) end is refused.
 */
export function resolveNervRange(startDate: unknown, endDate: unknown, now: Date = new Date()): NervRangeResult {
  if (!isCalendarYmd(startDate) || !isCalendarYmd(endDate)) {
    return { ok: false, error: 'startDate and endDate must be YYYY-MM-DD dates' };
  }
  const end = clampAsOfToYesterday(endDate, now);
  if (startDate > end) return { ok: false, error: 'startDate must not be after endDate or later than yesterday' };
  if (daysBetween(startDate, end) + 1 > MAX_VARIANT_HISTORY_NERV_DAYS) {
    return { ok: false, error: `The range may cover at most ${MAX_VARIANT_HISTORY_NERV_DAYS} days` };
  }
  return { ok: true, startDate, endDate: end };
}

/** The default chart range: the last 7 days, inclusive, ending yesterday PT. */
export function defaultNervRange(now: Date = new Date()): { startDate: string; endDate: string } {
  const endDate = ptYesterday(now);
  return { startDate: addDays(endDate, -(DEFAULT_NERV_RANGE_DAYS - 1)), endDate };
}

const asFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * One point for every day from `startDate` to `endDate` (inclusive). A day is plotted only
 * when its stored snapshot is `recorded` and carries a ∑: NERV; every other day (a
 * `not_recorded` row, no row, or a row without a figure) is a null gap. Nothing is estimated.
 */
export function buildNervChartPoints(startDate: string, endDate: string, rows: VariantHistoryNervRow[]): NervChartPoint[] {
  if (!isCalendarYmd(startDate) || !isCalendarYmd(endDate) || startDate > endDate) return [];
  const byDate = new Map<string, VariantHistoryNervRow>();
  for (const row of rows) byDate.set(row.snapshot_date, row);

  const points: NervChartPoint[] = [];
  for (let date = startDate; date <= endDate; date = addDays(date, 1)) {
    const row = byDate.get(date);
    const nerv = row?.status === 'recorded' ? asFiniteNumber(row.total_nerv) : null;
    if (row && nerv !== null) {
      points.push({ date, nerv, drPercentage: asFiniteNumber(row.dr_sku_percentage), gap: null });
    } else {
      points.push({
        date,
        nerv: null,
        drPercentage: null,
        gap: row ? { kind: 'not_recorded', reason: row.reason ?? null } : { kind: 'no_snapshot', reason: null },
      });
    }
  }
  return points;
}
